import type { AuraEmitter, AuraFx, WearMotion } from "@nano/content";

/**
 * Pure, time-based animation shared by the Phaser avatar and the DOM
 * preview: worn items that move (wings flap, capes trail, props sway, a
 * balloon bobs) and aura particles. Everything is a function of the clock,
 * so both renderers show the same motion without keeping particle state.
 */

export interface WalkState {
  /** Walk cycle phase in radians. */
  readonly phase: number;
  /** 0 standing .. 1 walking, eased. */
  readonly blend: number;
  /** Current speed as a fraction of full walking speed. */
  readonly speed: number;
}

export const STANDING: WalkState = { phase: 0, blend: 0, speed: 0 };

export interface WearPose {
  /** Degrees, added to the piece's base rotation. */
  readonly angle: number;
  /** Multipliers on the piece's base scale. */
  readonly sx: number;
  readonly sy: number;
}

const REST: WearPose = { angle: 0, sx: 1, sy: 1 };
const TAU = Math.PI * 2;

export function wearMotion(motion: WearMotion | undefined, t: number, walk: WalkState, side: boolean): WearPose {
  const { phase, blend, speed } = walk;
  switch (motion) {
    case "flap": {
      // Slow idle beat, quicker while walking; wings fold toward the back and open again.
      const beat = 0.5 + 0.5 * Math.sin(TAU * (0.9 + 1.1 * blend) * t);
      return side ? { angle: -12 * beat, sx: 1, sy: 1 - 0.04 * beat } : { angle: 0, sx: 1 - 0.2 * beat, sy: 1 + 0.02 * beat };
    }
    case "cape": {
      // Swings with the steps and trails behind when walking in profile.
      const trail = side ? 10 * blend * Math.min(1, speed * 1.5) : 0;
      return { angle: Math.sin(phase) * 2.5 * blend + trail + Math.sin(t * 1.7) * 0.8, sx: 1, sy: 1 + 0.03 * blend * Math.abs(Math.sin(phase)) };
    }
    case "sway":
      return { angle: Math.sin(phase) * 5 * blend + Math.sin(t * 1.6) * 2, sx: 1, sy: 1 };
    case "float":
      // A balloon on its string: pendulum drift, a little bob, leaning back when walking.
      return { angle: Math.sin(t * 1.1) * 7 + Math.sin(t * 0.43) * 3 - 6 * blend, sx: 1, sy: 1 + Math.sin(t * 2.3) * 0.02 };
    default:
      return REST;
  }
}

export interface AuraParticle {
  /** Index into `AuraFx.frames`. */
  readonly frame: number;
  /** Centre, relative to the aura centre, in body heights. */
  readonly x: number;
  readonly y: number;
  /** Displayed width of the particle, in body heights. */
  readonly size: number;
  readonly alpha: number;
  /** Degrees. */
  readonly angle: number;
  /** In front of the body (otherwise behind it). */
  readonly front: boolean;
}

/** Deterministic 0..1 noise from integers. */
function rand(a: number, b: number, c: number): number {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smooth = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));

/** Where the feet are, relative to the aura centre (body origin is at 0.96). */
export const AURA_FEET_Y = 0.41;

function particle(e: AuraEmitter, ei: number, i: number, tMs: number): AuraParticle {
  const life = lerp(e.life[0], e.life[1], rand(ei, i, 1));
  const r = (n: number, k: number) => rand(ei * 97 + i, n, k);
  if (e.mode === "orbit") {
    const a = TAU * (tMs / life + i / e.count);
    const depth = Math.sin(a);
    return {
      frame: e.frames[i % e.frames.length]!,
      x: Math.cos(a) * 0.46,
      y: 0.02 + depth * 0.1 + Math.sin(tMs / 500 + i) * 0.015,
      size: lerp(e.size[0], e.size[1], r(0, 2)) * (0.85 + 0.2 * depth),
      alpha: 0.75 + 0.25 * depth,
      angle: Math.sin(tMs / 700 + i) * 8,
      front: depth > 0,
    };
  }
  // Staggered so particles never pop in all at once.
  const clock = tMs + r(0, 3) * life;
  const n = Math.floor(clock / life);
  const p = clock / life - n;
  const frame = e.frames[Math.floor(r(n, 4) * e.frames.length)]!;
  const size = lerp(e.size[0], e.size[1], r(n, 5));
  const front = r(n, 6) < 0.5;
  const spin = r(n, 7) < 0.5 ? -1 : 1;
  switch (e.mode) {
    case "twinkle": {
      const a = r(n, 8) * TAU;
      const d = Math.sqrt(r(n, 9));
      const pop = Math.sin(Math.PI * p);
      return { frame, x: Math.cos(a) * 0.42 * d, y: Math.sin(a) * 0.5 * d - 0.05, size: size * pop, alpha: pop, angle: spin * (p - 0.5) * 50, front };
    }
    case "rise": {
      const fade = smooth(p / 0.15) * (1 - smooth((p - 0.6) / 0.4));
      return {
        frame,
        x: lerp(-0.38, 0.38, r(n, 8)) + Math.sin(p * TAU + r(n, 9) * TAU) * 0.05,
        y: lerp(0.1, 0.38, r(n, 10)) - p * 0.65,
        size: size * (0.7 + 0.3 * p),
        alpha: fade,
        angle: Math.sin(p * TAU) * 12,
        front,
      };
    }
    case "fall": {
      const fade = smooth(p / 0.12) * (1 - smooth((p - 0.8) / 0.2));
      return {
        frame,
        x: lerp(-0.5, 0.5, r(n, 8)) + Math.sin(p * Math.PI * 3 + r(n, 9) * TAU) * 0.06,
        y: lerp(-0.62, AURA_FEET_Y, p),
        size,
        alpha: fade,
        angle: spin * p * 200,
        front,
      };
    }
  }
}

/** All particles of an aura at time `tMs`. */
export function auraParticles(fx: AuraFx, tMs: number): AuraParticle[] {
  const out: AuraParticle[] = [];
  fx.emitters.forEach((e, ei) => {
    for (let i = 0; i < e.count; i++) out.push(particle(e, ei, i, tMs));
  });
  return out;
}

/** Ground glow pulse, 0..1. */
export function auraGlow(tMs: number): number {
  return 0.5 + 0.5 * Math.sin(tMs / 650);
}
